/**
 * The group dashboard's period filter is persisted in the URL as synthetic
 * tokens — `year-2025` (a whole calendar year) or `month-2025-03` (one month).
 * This module is the single definition of that grammar: it parses tokens into a
 * month range and serialises a range back. Both the period picker and
 * `usePeriodDateRange` consume it, so the format lives in exactly one place.
 */

/** A calendar month, identified by year + 1-based month (1–12). */
export type MonthKey = { year: number; month: number };
export type MonthRange = { start: MonthKey; end: MonthKey };

const YEAR_PATTERN = /^year-(\d{4})$/;
const MONTH_PATTERN = /^month-(\d{4})-(\d{2})$/;

/** Sortable ordinal so months compare with `<` / `>`. */
export function monthOrdinal({ year, month }: MonthKey): number {
  return year * 12 + (month - 1);
}

export function sameMonth(a: MonthKey, b: MonthKey): boolean {
  return a.year === b.year && a.month === b.month;
}

/** The `month-YYYY-MM` token for a single month. */
export function monthToken({ year, month }: MonthKey): string {
  return `month-${year}-${String(month).padStart(2, '0')}`;
}

/** The first and last month a single token spans, or null if unrecognised. */
function tokenSpan(token: string): MonthRange | null {
  const yearMatch = token.match(YEAR_PATTERN);
  if (yearMatch) {
    const year = Number(yearMatch[1]);
    return { start: { year, month: 1 }, end: { year, month: 12 } };
  }
  const monthMatch = token.match(MONTH_PATTERN);
  if (monthMatch) {
    const month = { year: Number(monthMatch[1]), month: Number(monthMatch[2]) };
    return { start: month, end: month };
  }
  return null;
}

/**
 * Collapse a list of period tokens to the month range they span — the earliest
 * month across all tokens to the latest (min-start / max-end). Returns null
 * when nothing parses. This is the one place that defines how a selection is
 * interpreted, and it keeps old `year-*` links and the default working.
 */
export function periodsToMonthRange(periods: string[]): MonthRange | null {
  let start: MonthKey | null = null;
  let end: MonthKey | null = null;
  for (const token of periods) {
    const span = tokenSpan(token);
    if (!span) continue;
    if (!start || monthOrdinal(span.start) < monthOrdinal(start)) {
      start = span.start;
    }
    if (!end || monthOrdinal(span.end) > monthOrdinal(end)) {
      end = span.end;
    }
  }
  return start && end ? { start, end } : null;
}

/**
 * Serialise a month range to period tokens — just the two endpoints, since
 * `periodsToMonthRange` re-derives the full span from min-start / max-end.
 */
export function monthRangeToPeriods({ start, end }: MonthRange): string[] {
  return sameMonth(start, end)
    ? [monthToken(start)]
    : [monthToken(start), monthToken(end)];
}
